import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowForward,
  Assignment,
  Close,
  DeleteOutline,
  Description,
  Edit,
  ExpandLess,
  ExpandMore,
  FormatListBulleted,
  NotificationsNone,
  Search,
  AccountTree,
  Visibility,
  VisibilityOff,
  WarningAmber,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import Navbar from "../../shared/Navbar";
import Breadcrumb from "../../shared/Breadcrumb";
import api from "../../../services/axios/api-service";
import { ENDPOINTS } from "../../../services/endpoints";
import { fetchProjectDropdownsByUser } from "../../../store";
import {
  STRATEGIST_MODEL_SETTINGS,
  USER_ROLES,
} from "../../../shared/appConstants";
import { COLORS } from "../../../shared/colors";
const {
  AI_PROVIDER_OPTIONS,
  FORM_FIELDS,
  KEY: STRATEGIST_SETTINGS_KEY,
} = STRATEGIST_MODEL_SETTINGS;
const ORKA_FLOW = [
  {
    labelKey: "requirement",
    icon: Description,
    color: COLORS.strategistAgent.workflow.requirement.text,
    background: COLORS.strategistAgent.workflow.requirement.background,
  },
  {
    labelKey: "epic",
    icon: Assignment,
    color: COLORS.strategistAgent.workflow.epic.text,
    background: COLORS.strategistAgent.workflow.epic.background,
  },
  {
    labelKey: "feature",
    icon: AccountTree,
    color: COLORS.strategistAgent.workflow.feature.text,
    background: COLORS.strategistAgent.workflow.feature.background,
  },
  {
    labelKey: "task",
    icon: Assignment,
    color: COLORS.strategistAgent.workflow.task.text,
    background: COLORS.strategistAgent.workflow.task.background,
  },
  {
    labelKey: "subtask",
    icon: FormatListBulleted,
    color: COLORS.strategistAgent.workflow.subtask.text,
    background: COLORS.strategistAgent.workflow.subtask.background,
  },
];

const createFreshForm = () => ({
  id: null,
  projectId: "",
  name: "",
  provider: "",
  version: "",
  apiKey: "",
});
const newId = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

export default function StrategistModelSettings() {
  const { t } = useTranslation();
  const settingsRequestId = useRef(0);
  const mask = (key) =>
    key
      ? `${"*".repeat(Math.max(0, key.length - 4))}${key.slice(-4)}`
      : t("strategistAgent.apiKeyHidden");
  const dispatch = useDispatch();
  const { projectDropdownsByUser: projects = [] } = useSelector(
    (s) => s.tickets,
  );
  const allowed = [USER_ROLES.ADMIN, USER_ROLES.PM].includes(
    useSelector((s) => s.auth.user?.role),
  );

  const [models, setModels] = useState({});
  const [botEnabled, setBotEnabled] = useState({});
  const [loading, setLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [form, setForm] = useState(createFreshForm);
  const [originalForm, setOriginalForm] = useState(createFreshForm);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [switchTarget, setSwitchTarget] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [projectSearch, setProjectSearch] = useState("");

  const getErrorMessage = useCallback(
    (error, fallbackKey) =>
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t(fallbackKey),
    [t],
  );

  useEffect(() => {
    if (allowed) dispatch(fetchProjectDropdownsByUser());
  }, [allowed, dispatch]);

  const projectIdsStr = projects.map((p) => p.id).sort().join(",");

  useEffect(() => {
    const requestId = ++settingsRequestId.current;
    if (!allowed) return;
    if (!projects.length) {
      setModels({});
      setBotEnabled({});
      setLoading(false);
      return;
    }

    setLoading(true);

    api
      .get(
        ENDPOINTS.GET_SETTINGS_BY_PROJECT_IDS(
          STRATEGIST_SETTINGS_KEY,
          projects.map((p) => p.id),
        ),
      )
      .then((response) => {
        if (requestId !== settingsRequestId.current) return;
        const settingsByProject = Object.fromEntries(
          (response.data?.data || []).map((setting) => [
            setting.project_id,
            setting.json_value || {},
          ]),
        );
        setModels(
          Object.fromEntries(
            projects.map((p) => [
              p.id,
              settingsByProject[p.id]?.api_models || [],
            ]),
          ),
        );
        setBotEnabled(
          Object.fromEntries(
            projects.map((p) => [
              p.id,
              settingsByProject[p.id]?.bot_enabled !== false,
            ]),
          ),
        );
        setLoading(false);
      })
      .catch((error) => {
        if (requestId !== settingsRequestId.current) return;
        setModels(Object.fromEntries(projects.map((p) => [p.id, []])));
        setBotEnabled(Object.fromEntries(projects.map((p) => [p.id, true])));
        setLoading(false);
        toast.error(getErrorMessage(error, "strategistAgent.unableToUpdate"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, getErrorMessage, projectIdsStr]);

  // The API persists one JSON document per project. This updater derives the
  // next model collection from the latest project state before saving it.
  const updateProjectSettings = useCallback(
    async (
      projectId,
      updater,
      strategistEnabled = botEnabled[projectId] !== false,
    ) => {
      const previous = models[projectId] || [];
      const next = typeof updater === "function" ? updater(previous) : updater;

      // Keep the current active state visible until the server confirms the change.
      await api.put(ENDPOINTS.UPDATE_SETTING(STRATEGIST_SETTINGS_KEY), {
        project_id: projectId,
        json_value: { api_models: next, bot_enabled: strategistEnabled },
      });
      setModels((old) => ({ ...old, [projectId]: next }));
      setBotEnabled((old) => ({ ...old, [projectId]: strategistEnabled }));
    },
    [botEnabled, models],
  );

  const field = (name) => (e) =>
    setForm((old) => ({
      ...old,
      [name]: e.target.value,
      ...(name === FORM_FIELDS.PROVIDER ? { [FORM_FIELDS.VERSION]: "" } : {}),
    }));

  const formComplete = Boolean(
    form.projectId &&
    form.name.trim() &&
    form.provider &&
    form.version &&
    form.apiKey.trim(),
  );

  const formChanged =
    !form.id ||
    ["name", "provider", "version", "apiKey"].some(
      (key) => form[key] !== originalForm[key],
    );

  const save = async () => {
    if (
      !form.projectId ||
      !form.name.trim() ||
      !form.provider ||
      !form.version ||
      !form.apiKey.trim()
    )
      return toast.error(t("strategistAgent.completeRequiredFields"));

    const list = models[form.projectId] || [];

    const isDuplicate = list.some(
      (x) =>
        x.id !== form.id &&
        x.configuration_name.trim().toLowerCase() === form.name.trim().toLowerCase()
    );

    if (isDuplicate) {
      return toast.error(t("strategistAgent.configurationNameExists"));
    }

    const current = list.find((x) => x.id === form.id);
    const model = {
      id: form.id || newId(),
      configuration_name: form.name.trim(),
      ai_provider: form.provider,
      model_version: form.version,
      // Existing providers may no longer be selectable, but their saved
      // configuration must remain editable without losing its model type.
      model_type:
        AI_PROVIDER_OPTIONS[form.provider]?.type || current?.model_type,
      // New configurations stay disabled until the user explicitly activates them.
      enabled: current?.enabled ?? false,
      ...(form.apiKey.trim() ? { api_key: form.apiKey.trim() } : {}),
    };

    const next = current
      ? list.map((x) => (x.id === model.id ? { ...x, ...model } : x))
      : [...list, model];

    try {
      setSaving(true);
      await updateProjectSettings(
        form.projectId,
        next,
        botEnabled[form.projectId] !== false,
      );
      setOpen(false);
      toast.success(t("strategistAgent.configurationSaved"));
    } catch (e) {
      toast.error(getErrorMessage(e, "strategistAgent.unableToSave"));
    } finally {
      setSaving(false);
    }
  };

  const activateApi = async (projectId, model) => {
    const list = models[projectId] || [];
    const next = list.map((x) => ({ ...x, enabled: x.id === model.id }));
    try {
      await updateProjectSettings(projectId, next, false);
    } catch (e) {
      toast.error(getErrorMessage(e, "strategistAgent.unableToUpdate"));
    }
  };
  
  const activateBot = async (projectId) => {
    try {
      await updateProjectSettings(
        projectId,
        (models[projectId] || []).map((model) => ({
          ...model,
          enabled: false,
        })),
        true,
      );
    } catch (e) {
      toast.error(getErrorMessage(e, "strategistAgent.unableToUpdate"));
    }
  };

  const toggle = async (projectId, model) => {
    const list = models[projectId] || [];
    if (!model.enabled) {
      const activeModel = list.find((x) => x.enabled);
      const activeName =
        activeModel?.configuration_name ||
        (botEnabled[projectId] !== false
          ? t("strategistAgent.orkaName")
          : null);
      if (activeName) {
        setSwitchTarget({ projectId, model, activeName });
        return;
      }
      return activateApi(projectId, model);
    }

    setSwitchTarget({
      projectId,
      activeName: model.configuration_name,
      targetName: t("strategistAgent.orkaName"),
      type: "bot",
    });
  };

  const toggleBot = async (projectId, enabled) => {
    const botShouldBeEnabled = !enabled;
    const next = botShouldBeEnabled
      ? (models[projectId] || []).map((x) => ({ ...x, enabled: false }))
      : models[projectId] || [];
    if (
      !botShouldBeEnabled &&
      !(models[projectId] || []).some((model) => model.enabled)
    )
      return;
    const activeModel = (models[projectId] || []).find(
      (model) => model.enabled,
    );
    if (botShouldBeEnabled && activeModel) {
      setSwitchTarget({
        projectId,
        activeName: activeModel.configuration_name,
        targetName: t("strategistAgent.orkaName"),
        type: "bot",
      });
      return;
    }
    try {
      await updateProjectSettings(projectId, next, botShouldBeEnabled);
    } catch (e) {
      toast.error(getErrorMessage(e, "strategistAgent.unableToUpdate"));
    }
  };

  const remove = async () => {
    const { projectId, model } = removeTarget;
    const configurationName = model.configuration_name;
    const next = (models[projectId] || []).filter((x) => x.id !== model.id);
    try {
      await updateProjectSettings(
        projectId,
        next,
        next.some((x) => x.enabled) ? false : true,
      );
      setRemoveTarget(null);
      toast.success(
        t("strategistAgent.configurationDeleted", { name: configurationName }),
      );
    } catch (e) {
      toast.error(getErrorMessage(e, "strategistAgent.unableToDelete"));
    }
  };

  if (!allowed)
    return (
      <div className="dashboard">
        <Navbar />
        <main className="main">
          <Box
            p={{ xs: 1, md: 2 }}
            sx={{
              border: `1px solid ${COLORS.brand.primaryDark}`,
              borderRadius: 1,
              m: { xs: 1, md: 2 },
              bgcolor: COLORS.white,
            }}
          >
            {t("strategistAgent.accessDenied")}
          </Box>
        </main>
      </div>
    );

  return (
    <div className="dashboard">
      <Navbar />
      <main className="main">
        <Breadcrumb
          items={[
            { label: t("settingsPage.title"), path: "/settings" },
            { label: t("strategistAgent.breadcrumb"), path: null },
          ]}
        />
        <Box p={{ xs: 1.5, md: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            gap={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {t("strategistAgent.heading")}
              </Typography>
              <Typography color="text.secondary">
                {t("settingsPage.strategistAgent.description")}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                const nextForm = createFreshForm();
                setForm(nextForm);
                setOriginalForm(nextForm);
                setShowApiKey(false);
                setOpen(true);
              }}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("strategistAgent.createNewApi")}
            </Button>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            projects.map((p) => {
              const list = models[p.id] || [];
            const bot = botEnabled[p.id] !== false;
            const showOrkaFlow = !list.some((model) => model.api_key);
            const expanded = expandedProjects[p.id] !== false;
            return (
              <Card
                key={p.id}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  borderColor: COLORS.strategistAgent.projectBorder,
                  borderRadius: 1.5,
                  boxShadow: "none",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      setExpandedProjects((old) => ({
                        ...old,
                        [p.id]: !expanded,
                      }))
                    }
                    aria-expanded={expanded}
                    sx={{
                      border: 0,
                      cursor: "pointer",
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      textAlign: "left",
                      px: 1.25,
                      py: 1,
                      bgcolor: COLORS.strategistAgent.projectHeader,
                      borderRadius: 1,
                      color: "text.primary",
                    }}
                  >
                    <NotificationsNone sx={{ fontSize: 18, mr: 1 }} />
                    <Typography fontWeight={600} flexGrow={1}>
                      {p.name || p.project_name}
                    </Typography>
                    {expanded ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </Box>
                  <Collapse in={expanded}>
                    <Box
                      sx={{
                        px: { xs: 1, md: 2 },
                        py: 2,
                        borderBottom: list.length
                          ? `1px solid ${COLORS.strategistAgent.projectDivider}`
                          : 0,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={2}
                      >
                        <Box>
                          <Typography color="primary" fontWeight={500}>
                            {t("strategistAgent.orkaName")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5, maxWidth: 500 }}
                          >
                            {t("strategistAgent.orkaDescription")}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" flexShrink={0}>
                          <Switch
                            checked={bot}
                            onChange={() => toggleBot(p.id, bot)}
                            disabled={
                              bot && !list.some((model) => model.enabled)
                            }
                            inputProps={{
                              "aria-label": t("strategistAgent.toggleOrka", {
                                projectName: p.name || p.project_name,
                              }),
                            }}
                          />
                          <Typography variant="body2" fontWeight={500} sx={{ width: 56 }}>
                            {bot
                              ? t("strategistAgent.enabled")
                              : t("strategistAgent.disabled")}
                          </Typography>
                        </Box>
                      </Box>
                      {showOrkaFlow && (
                        <Box
                          aria-label={t("strategistAgent.orkaWorkflow")}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1.5,
                          }}
                        >
                          {ORKA_FLOW.map((step, index) => {
                            const Icon = step.icon;
                            return (
                              <React.Fragment key={step.labelKey}>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: 0.5,
                                    color: step.color,
                                    bgcolor: step.background,
                                    fontSize: 12,
                                  }}
                                >
                                  <Icon sx={{ fontSize: 16 }} />
                                  {t(`strategistAgent.${step.labelKey}`)}
                                </Box>
                                {index < ORKA_FLOW.length - 1 && (
                                  <ArrowForward
                                    sx={{ fontSize: 18, color: "text.primary" }}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                    {list.map((x) => (
                      <Box
                        key={x.id}
                        px={2.75}
                        py={1.75}
                        mt={1.5}
                        border="1px solid"
                        borderColor={
                          x.enabled
                            ? COLORS.strategistAgent.activeBorder
                            : COLORS.strategistAgent.inactiveBorder
                        }
                        borderLeftWidth={4}
                        borderLeftColor={
                          x.enabled
                            ? COLORS.strategistAgent.activeBorder
                            : COLORS.strategistAgent.inactiveBorder
                        }
                        borderRadius={1.25}
                        sx={{
                          borderLeft: x.enabled
                            ? `4px solid ${COLORS.strategistAgent.activeBorder}`
                            : `4px solid ${COLORS.strategistAgent.inactiveBorder}`,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={2}
                          flexWrap="wrap"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                          >
                            <Typography fontWeight={600}>
                              {x.configuration_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                x.enabled
                                  ? t("strategistAgent.enabled")
                                  : t("strategistAgent.disabled")
                              }
                              color={x.enabled ? "success" : "default"}
                              variant={x.enabled ? "outlined" : "filled"}
                              sx={
                                x.enabled
                                  ? undefined
                                  : {
                                      bgcolor:
                                        COLORS.strategistAgent
                                          .inactiveChipBackground,
                                      color:
                                        COLORS.strategistAgent.inactiveChipText,
                                      fontWeight: 500,
                                    }
                              }
                            />
                          </Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            flexShrink={0}
                          >
                            <IconButton
                              color="primary"
                              aria-label={t(
                                "strategistAgent.editConfiguration",
                                { name: x.configuration_name },
                              )}
                              onClick={() => {
                                // Retain the secret returned by the settings API so editing
                                // non-secret fields never clears the stored API key.
                                const existing = (models[p.id] || []).find(
                                  (item) => item.id === x.id,
                                );
                                const nextForm = {
                                  id: existing?.id || x.id,
                                  projectId: String(p.id),
                                  name:
                                    existing?.configuration_name ||
                                    x.configuration_name,
                                  provider:
                                    existing?.ai_provider || x.ai_provider,
                                  version:
                                    existing?.model_version || x.model_version,
                                  apiKey: existing?.api_key || "",
                                };
                                setForm(nextForm);
                                setOriginalForm(nextForm);
                                setShowApiKey(false);
                                setOpen(true);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              color="primary"
                              aria-label={t(
                                "strategistAgent.deleteConfigurationAria",
                                { name: x.configuration_name },
                              )}
                              onClick={() =>
                                setRemoveTarget({ projectId: p.id, model: x })
                              }
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                            <Switch
                              checked={!!x.enabled}
                              onChange={() => toggle(p.id, x)}
                              inputProps={{
                                "aria-label": t(
                                  "strategistAgent.toggleConfiguration",
                                  { name: x.configuration_name },
                                ),
                              }}
                            />
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ width: 56 }}
                            >
                              {x.enabled
                                ? t("strategistAgent.enabled")
                                : t("strategistAgent.disabled")}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            borderTop: `1px solid ${COLORS.ui.border}`,
                            my: 1,
                            mx: -1.25,
                          }}
                        />
                        <Typography variant="body2">
                          <b>{t("strategistAgent.aiProviderLabel")}</b>{" "}
                          {x.ai_provider || t("strategistAgent.notSpecified")}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <b>{t("strategistAgent.apiKeyLabel")}</b>{" "}
                          {mask(x.api_key)}
                        </Typography>
                      </Box>
                    ))}
                  </Collapse>
                </CardContent>
              </Card>
            );
          })
          )}
        </Box>
        <Dialog
          open={open}
          onClose={() => !saving && setOpen(false)}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              width: "min(900px, calc(100% - 32px))",
              minHeight: 530,
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ px: 4, pt: 6, pb: 3 }}>
            {form.id
              ? t("strategistAgent.editApiModel")
              : t("strategistAgent.createNewApiModel")}
          </DialogTitle>
          <DialogContent sx={{ px: 4, py: 2, display: "flex" }}>
            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              columnGap={3}
              rowGap={3}
              width="100%"
              pt={1.5}
              mt={4}
            >
              <TextField
                label={t("strategistAgent.configurationName")}
                placeholder={t("strategistAgent.enterModelName")}
                value={form.name}
                onChange={field("name")}
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 75 }}
                helperText={`${form.name.length}/75 characters`}
              />
              <FormControl required>
                <InputLabel shrink>
                  {t("strategistAgent.projectName")}
                </InputLabel>
                <Select
                  label={t("strategistAgent.projectName")}
                  value={form.projectId}
                  onChange={field("projectId")}
                  disabled={!!form.id}
                  displayEmpty
                  renderValue={(selected) => {
                    const project = projects.find(
                      (item) => String(item.id) === String(selected),
                    );
                    return project
                      ? project.name || project.project_name
                      : t("strategistAgent.projectName");
                  }}
                  onOpen={() => setProjectSearch("")}
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 340, width: 300, mt: 0.5 } },
                    MenuListProps: { sx: { py: 0.5 } },
                  }}
                >
                  <ListSubheader sx={{ bgcolor: "background.paper", py: 1 }}>
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      value={projectSearch}
                      placeholder={t("strategistAgent.searchProjects")}
                      onChange={(event) => setProjectSearch(event.target.value)}
                      onKeyDown={(event) => event.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  {projects
                    .filter((project) =>
                      (project.name || project.project_name || "")
                        .toLowerCase()
                        .includes(projectSearch.trim().toLowerCase()),
                    )
                    .map((project) => (
                      <MenuItem
                        key={project.id}
                        value={String(project.id)}
                        sx={{ whiteSpace: "normal", minHeight: 42 }}
                      >
                        {project.name || project.project_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl required>
                <InputLabel shrink>
                  {t("strategistAgent.aiProvider")}
                </InputLabel>
                <Select
                  label={t("strategistAgent.aiProvider")}
                  value={form.provider}
                  onChange={field("provider")}
                  displayEmpty
                  renderValue={(selected) =>
                    selected || t("strategistAgent.aiProvider")
                  }
                >
                  {Object.keys(AI_PROVIDER_OPTIONS).map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl required disabled={!form.provider}>
                <InputLabel shrink>
                  {t("strategistAgent.modelVersion")}
                </InputLabel>
                <Select
                  label={t("strategistAgent.modelVersion")}
                  value={form.version}
                  onChange={field("version")}
                  displayEmpty
                  renderValue={(selected) =>
                    selected || t("strategistAgent.modelVersion")
                  }
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 200, mt: 0.5 } },
                    MenuListProps: { sx: { py: 0.5 } },
                  }}
                >
                  {(AI_PROVIDER_OPTIONS[form.provider]?.models || []).map(
                    (v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
              <TextField
                sx={{ gridColumn: "1 / -1" }}
                label={t("strategistAgent.providerApiKey")}
                type={showApiKey ? "text" : "password"}
                name="strategist-provider-api-key"
                autoComplete="off"
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                }}
                value={form.apiKey}
                onChange={field("apiKey")}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle api key visibility"
                        onClick={() => setShowApiKey((show) => !show)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 4, pt: 1, gap: 1 }}>
            <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button
              variant="contained"
              disabled={saving || !formComplete || !formChanged}
              onClick={save}
            >
              {form.id ? t("common.update") : t("common.create")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!switchTarget}
          onClose={() => !switching && setSwitchTarget(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { width: 444, borderRadius: 2, textAlign: "center" },
          }}
        >
          <IconButton
            aria-label={t("strategistAgent.close")}
            onClick={() => !switching && setSwitchTarget(null)}
            disabled={switching}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 1,
              color: "text.secondary",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <DialogContent sx={{ px: 4, pt: 4, pb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
                borderRadius: "50%",
                bgcolor: COLORS.strategistAgent.warningBackground,
              }}
            >
              <WarningAmber
                sx={{ color: COLORS.strategistAgent.warning, fontSize: 31 }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: 21 }}>
              {t("strategistAgent.anotherApiEnabled")}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.5 }}>
              <Trans
                i18nKey="strategistAgent.switchActiveMessage"
                values={{
                  activeName: switchTarget?.activeName,
                  targetName:
                    switchTarget?.targetName ||
                    switchTarget?.model.configuration_name,
                }}
                components={{
                  highlight: (
                    <Box
                      component="span"
                      sx={{
                        color: COLORS.strategistAgent.warning,
                        fontWeight: 700,
                      }}
                    />
                  ),
                  strong: <strong />,
                }}
              />
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3, pt: 1 }}>
            <Button
              variant="contained"
              disabled={switching}
              sx={{
                minWidth: 100,
                bgcolor: COLORS.strategistAgent.warning,
                "&:hover": { bgcolor: COLORS.strategistAgent.warningHover },
                "&.Mui-disabled": {
                  bgcolor: COLORS.strategistAgent.warning,
                  color: COLORS.white,
                  opacity: 0.8,
                },
              }}
              onClick={async () => {
                const target = switchTarget;
                setSwitching(true);
                try {
                  if (target.type === "bot") {
                    await activateBot(target.projectId);
                  } else {
                    await activateApi(target.projectId, target.model);
                  }
                  setSwitchTarget(null);
                } finally {
                  setSwitching(false);
                }
              }}
            >
              {switching ? <CircularProgress size={20} color="inherit" /> : t("strategistAgent.gotIt")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={!!removeTarget}
          onClose={() => !deleting && setRemoveTarget(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { width: 448, borderRadius: 2, textAlign: "center" },
          }}
        >
          <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
                borderRadius: "50%",
                bgcolor: COLORS.strategistAgent.deleteBackground,
                color: COLORS.strategistAgent.delete,
              }}
            >
              <DeleteOutline sx={{ fontSize: 30 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: 21 }}>
              {t("strategistAgent.deleteConfiguration")}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {t("strategistAgent.deleteConfirmation")}
            </Typography>
            <Box
              sx={{
                mt: 1.75,
                px: 2,
                py: 1.75,
                border: `1px solid ${COLORS.strategistAgent.deleteNameBorder}`,
                borderRadius: 1,
                bgcolor: COLORS.strategistAgent.deleteNameBackground,
                color: COLORS.strategistAgent.delete,
                fontWeight: 700,
              }}
            >
              {removeTarget?.model.configuration_name}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2 }}
            >
              {t("strategistAgent.cannotUndo")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 4, pt: 1.75, gap: 1.5 }}>
            <Button
              variant="outlined"
              fullWidth
              disabled={deleting}
              onClick={() => setRemoveTarget(null)}
              sx={{
                color: "text.primary",
                borderColor: COLORS.strategistAgent.cancelBorder,
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await remove();
                } finally {
                  setDeleting(false);
                }
              }}
              sx={{
                bgcolor: COLORS.strategistAgent.delete,
                "&:hover": { bgcolor: COLORS.strategistAgent.deleteHover },
                "&.Mui-disabled": {
                  bgcolor: COLORS.strategistAgent.delete,
                  color: COLORS.white,
                  opacity: 0.8,
                },
              }}
            >
              {deleting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("common.delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
}
